import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs/internal/Observable';
import { distinctUntilChanged, take, withLatestFrom } from 'rxjs/operators';
import { ICollectOrderDetailsFormValue } from 'src/app/inventory/interfaces/ICollectOrderDetailsFormValue';
import { IBulkAddState, IBulkAddStateNdcItem } from 'src/app/inventory/interfaces/bulk-add-state.interface';
import { deepCopy, groupByProperty, highlightRequiredControlsWithErrors, isEqual, valueIsNumber } from 'src/app/shared/helpers/helpers';
import { IntegrationTypeEnum } from 'src/app/shared/interfaces/integration.interface';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { AlertMessageService } from 'src/app/shared/services/alert-message.service';
import { EventsService } from 'src/app/shared/services/events.service';
import { GetBulkAddInventorySnapshot, SaveBulkAddInventorySnapshot } from 'src/app/shared/states/application/application.actions';
import { ApplicationState } from 'src/app/shared/states/application/application.state';
import {
  BulkGetInventoryOnLocation,
  ClearLoadedLibertyRxInventory,
  DisplayLoadLibertyRxInventoryCancelModal,
  DisplayLoadLibertyRxInventoryErrorModal,
  DisplayNoLibertySettingPopover,
  DisplayNoNewNdcPopover,
  GetInventoryOnLocation,
  LoadInventoryFromLibertyRx,
  RemoveLoadedLibertyRxInventoryRow,
  SetUserAcknowledgedLibertyRxLoadStatus
} from 'src/app/shared/states/inventory/inventory.actions';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { FormControls } from 'src/app/shared/utilities/form-controls.util';
import { BaseTableDirective } from '../../../../shared/directives/base-table.directive';
import { ActivityTypesEnum } from '../../../../shared/interfaces/ActivityTypesEnum';
import { INDC } from '../../../../shared/interfaces/INDC.interface';
import { IDrug } from '../../../../shared/interfaces/drug.interface';
import { ITransactionRequest } from '../../../../shared/interfaces/transaction-request.interface';
import { AddEDIOrder } from '../../../../shared/states/orders/orders.actions';
import { OrdersState } from '../../../../shared/states/orders/orders.state';
import { ProfileState } from '../../../../shared/states/profile/profile.state';
import { BackCountService } from 'src/app/shared/services/back-count.service';
import { DrugScheduleEnum } from 'src/app/shared/interfaces/DrugScheduleEnum ';
import { YesNoToggleComponent } from 'src/app/shared/components/yes-no-toggle/yes-no-toggle.component';
import { IBulkAddInventoryLocationAndBackCount } from 'src/app/inventory/interfaces/IBulkAddInventoryLocationAndBackCount';
import { BulkGetNdcsExistInUnconfirmedBackCount, IsUnconfirmedBackCountNdcExists } from 'src/app/shared/states/audits/audits.actions';
import { AuditsState } from 'src/app/shared/states/audits/audits.state';
import Decimal from 'decimal.js';
import { NUMBER_MIN_VALUE } from 'src/app/shared/constants/constants';

type IBulkAddInventoryLocationAndBackCountControl =  FormControls<IBulkAddInventoryLocationAndBackCount>;
@UntilDestroy()
@Component({
  selector: 'app-add-inventory-table',
  templateUrl: './add-inventory-table.component.html',
  styleUrls: ['./add-inventory-table.component.scss', './../../../inventory.scss'],
  providers: [BackCountService]
  
})
export class AddInventoryTableComponent extends BaseTableDirective implements OnInit, OnDestroy {

  @Select(OrdersState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(ProfileState.success) success$: Observable<boolean>;
  @Select(OrdersState.success) orderStateSuccess$: Observable<boolean>;
  @Select(InventoryState.loadingLibertyRxInventory) loadingLibertyRxInventory$: Observable<boolean>;
  @Select(InventoryState.displayNoLibertySettingPopover) displayNoLibertySettingPopover$: Observable<boolean>;
  @Select(InventoryState.displayNoNewNdcPopover) displayNoNewNdcPopover$: Observable<boolean>;
  @Select(InventoryState.displayLoadLibertyRxInventoryErrorModal) displayLoadLibertyRxInventoryErrorModal$: Observable<boolean>;
  @Select(InventoryState.displayLoadLibertyRxInventoryCancelModal) displayLoadLibertyRxInventoryCancelModal$: Observable<boolean>;
  @Select(PharmacyState.currentPharmacy) currentPharmacy$: Observable<any>;
  @Select(InventoryState.isLibertyRxIntegrationConfigurationOn) isLibertyRxIntegrationConfigurationOn$: Observable<boolean>;
  @Select(ProfileState.pharmacyManagementSystemIntegration) pharmacyManagementSystemIntegration$: Observable<boolean>;
  @Select(ApplicationState.bulkAddInventorySnapshot) bulkAddInventoryState$: Observable<IBulkAddState>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  addInventoryControlsForm: UntypedFormGroup;

  IntegrationTypeEnum = IntegrationTypeEnum;

  tableOptions = {
    asc: false,
  };

  ndcList: INDC[] = [];

  tableColumns = {
    name: true,
  };

  addingValues: number[] = [];
  editRow: boolean[] = [];

  currentOption: IDrug;

  focusPin: boolean;
  showPinDialog: boolean;
  showResetPinDialog: boolean;

  loading: boolean;

  focusDrugSearch: boolean;
  focusQuantity: boolean;

  displayOrderDetailsDialog: boolean;

  collectedOrderDetails: ICollectOrderDetailsFormValue;

  transactionDate: Date = new Date();

  displayFileUploader: boolean;

  inventoryLocationsEnabled: boolean;
  excludeOptions: string[][] = [];

  @ViewChild('backCountToggle') backCountToggle: YesNoToggleComponent;

  constructor(
    protected store: Store,
    private fb: UntypedFormBuilder,
    private router: Router,
    private formattedDatePipe: FormattedDatePipe, 
    private eventService: EventsService,
    private alertService: AlertMessageService,
    private backCountService: BackCountService
  ) {
    super(store);
    this.addInventoryControlsForm = this.fb.group({
      friendlyName: [{value: '', disabled: this.loading}, [Validators.required]],
      quantity: [{value: null, disabled: this.loading}, [Validators.required, Validators.min(NUMBER_MIN_VALUE)]],
      transactionDateDisplayValue: formattedDatePipe.transform(new Date(), 'MMM d a, HH:mm yyyy'),
      locationsAndBackCounts: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.focusDrugSearch = true;
    this.isLoading$
      .pipe(untilDestroyed(this))
      .subscribe((value: boolean) => {
        this.loading = value;
      });

      if (!this.store.selectSnapshot(ApplicationState.bulkAddInventorySnapshot)) {
        this.store.dispatch(new GetBulkAddInventorySnapshot());
      }

      this.bulkAddInventoryState$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(state => {
        if (state && !this.ndcList?.length) {
          this.ndcList = deepCopy(state.ndcs);
          if (state.orderDetails) {
            this.collectedOrderDetails = deepCopy(state.orderDetails);
          }

          if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
            (state.ndcs || []).map((x: IBulkAddStateNdcItem) => {
              this.appendLocationAndBackCount(x?.id, x.locationId, x.schedule, x.isBackCountMatches);
            });
          }
          this.bulkUpdateNdcsStartOnHand();
          this.bulkCheckNDCsInActiveBackCount();        
        }
      });
      this.eventService.logOutClicked$
      .pipe(take(1))
      .subscribe(() => {
        this.preserveState();
      })

      this.inventoryLocationsEnabled$
      .pipe(untilDestroyed(this))
      .subscribe(x => this.inventoryLocationsEnabled = x);
  }

  populateLoadedInventory(inventories) {
    if (inventories) {
      const copy = inventories
        .filter(obj => {
          // ignore anything that's already been added.
          return this.ndcList.findIndex(i => i.id === obj.id) === -1;
        })
        .map(obj => Object.assign({}, obj));

      copy.forEach(element => {
        element.locationId = this.store.selectSnapshot(InventoryState.primaryLocationID);
        setTimeout(() => {
          this.ndcList.push(element);
          if (this.inventoryLocationsEnabled) {
            this.appendLocationAndBackCount(element.id, this.store.selectSnapshot(InventoryState.primaryLocationID), element.schedule, null);
          }
        });
       
      });
    }
    setTimeout(() => {
      this.bulkUpdateNdcsStartOnHand();
      this.bulkCheckNDCsInActiveBackCount();
    });
  }
  

  private bulkUpdateNdcsStartOnHand(): void {
    if (this.ndcList.length) {
      this.store.dispatch(new BulkGetInventoryOnLocation(this.ndcList?.filter(x => x.locationId).map(x => ({drugId: x.id, locationId: x.locationId, gpi: x.gpi}))))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const bulkInventoryResponse = this.store.selectSnapshot(InventoryState.bulkInventoryForNdcOnLocation);
        this.ndcList.forEach(x => {
          const ndc = bulkInventoryResponse.find(y => y.locationID === x.locationId && y.drugID === x.id);
          if (ndc) {
            x.ndcAmount = ndc.onHand || 0;
            x.onHand = (x.adding || 0) + x.ndcAmount;
            x.similarOnHand = ndc.similarOnHand;
          } else {
            x.ndcAmount = 0;
            x.onHand = +x.adding;
            x.similarOnHand = 0;
          }
        });
        groupByProperty(this.ndcList.filter(x => !!x.gpi), 'gpi')?.map(x => this.adjustSOH(x.header));
      });
    }
  }
  

  saveCollectedOrderDetails(collectedOrderDetails: ICollectOrderDetailsFormValue): void {
    this.collectedOrderDetails = collectedOrderDetails;
    this.displayOrderDetailsDialog = false;
  }

  loadInventoryFromLibertyRx(): void {
    this.store.dispatch(new LoadInventoryFromLibertyRx());
  }

  addQuantity(): void {
    if (!this.addInventoryControlsForm.valid) {
      highlightRequiredControlsWithErrors(this.addInventoryControlsForm);
      return;
    }
    const ndc: INDC = {
      id: this.currentOption.id,
      name: this.currentOption.friendlyName,
      createDate: new Date(),
      packageNdc: this.currentOption.packageNdc,
      onHand: 0,
      receiving: 0,
      adding: +this.addInventoryControlsForm.get('quantity')?.value,
      isNew: false,
      locationId: this.store.selectSnapshot(InventoryState.primaryLocationID),
      schedule: this.currentOption.schedule,
      isBackCountMatches: null,
      gpi: this.currentOption.gpi,
      similarOnHand: this.currentOption.similarOnHand,
      availableSOH: null
    };
    const copy = this.ndcList.find(el => el.id === this.currentOption.id);

    if (!copy || (this.inventoryLocationsEnabled && this.isAnyAvailableLocation(ndc.id))) {
      this.ndcList.push(ndc);
      if (this.inventoryLocationsEnabled) {
        this.appendLocationAndBackCount(ndc.id, !copy ? this.store.selectSnapshot(InventoryState.primaryLocationID) : undefined, ndc.schedule);
        if (copy) {
          ndc.locationId = null;
        }
      }
    } else {
      copy.adding += +this.addInventoryControlsForm.get('quantity')?.value;
    }
    this.editRow.push(false);
    this.addInventoryControlsForm.controls.quantity.reset();
    this.addInventoryControlsForm.controls.friendlyName.reset();
    this.focusDrugSearch = true;

    if (ndc.locationId) {
      this.updateNdcStartOnHand(ndc.locationId, ndc.id, ndc.gpi);
    }
    this.checkNdcExistsInUnresolvedBackCount(ndc);
  }

    private adjustSOH(gpi: string): void {
      if (!!gpi) {
        const ndcs = this.ndcList
        .filter(ndc => ndc.gpi === gpi);
        if (ndcs?.length && !isNaN(ndcs[0].similarOnHand)) {
            const actualSOH = new Decimal(+ndcs[0].similarOnHand);
            let newSOH = ndcs
            .map(x => +x.adding)
            .reduce((acc, count) => acc.add(new Decimal(count)), actualSOH)
            .toNumber();
            ndcs.forEach(ndc => {
              if (!!ndc.gpi) {
                ndc.availableSOH = newSOH;
              }
            });
        }
      }
    }

  private updateNdcStartOnHand(locationId: string, drugId: string, gpi?: string): void {
    this.store.dispatch(new GetInventoryOnLocation(locationId, drugId, gpi))
    .pipe(untilDestroyed(this))
    .subscribe(x => {
       const inventoryResult = this.store.selectSnapshot(InventoryState.currentInventoryForNdcOnLocation);
       const ndc = this.ndcList.find(x => x.locationId === locationId && x.id === drugId);
       if (inventoryResult && ndc) {
         ndc.ndcAmount = inventoryResult.onHand || 0;
         ndc.onHand = (ndc.adding || 0) + ndc.ndcAmount;
         ndc.similarOnHand = valueIsNumber(inventoryResult.similarOnHand) ? inventoryResult.similarOnHand : ndc.similarOnHand || 0;
         this.adjustSOH(ndc.gpi);
       }
    })
  }

  private getLocationsFormValue(): IBulkAddInventoryLocationAndBackCount[] {
    return this.addInventoryControlsForm.getRawValue()?.locationsAndBackCounts as IBulkAddInventoryLocationAndBackCount[];
  }

  private isAnyAvailableLocation(drugId: string): boolean {
    const locationsAndBackCounts = this.getLocationsFormValue();
    return this.store.selectSnapshot(InventoryState.activeLocations)?.length > locationsAndBackCounts?.filter(x => x.drugId === drugId)?.length;
  }

  getLocationsToExcludeWhichAlreadySelectedForTheDropdown(rowN: number): string[] {
    const locationsAndBackCounts = this.getLocationsFormValue();
    const row = locationsAndBackCounts?.[rowN];
    return locationsAndBackCounts
      ?.filter(x => x.id !== row?.id && x.drugId === row?.drugId)
      ?.map(x => x?.id) || []
    ;
  }

  updateExcludeOptionsForAllRows() {
    const locationsArray = this.addInventoryControlsForm.get('locationsAndBackCounts') as FormArray;
    this.excludeOptions = locationsArray.controls.map((_, index) => this.getLocationsToExcludeWhichAlreadySelectedForTheDropdown(index));
  }

  openEditMode(index: number): void {
    this.editRow[index] = true;
    this.addingValues[index] = this.ndcList[index].adding || 0;
  }

  closeEditMode(i: number, save: boolean = true): void {
    if (save && +this.addingValues[i] > 0) {
      if (this.ndcList[i].adding != this.addingValues[i]) {
        this.ndcList[i].adding = +this.addingValues[i];
        this.ndcList[i].onHand = this.ndcList[i].adding + +this.ndcList[i].ndcAmount;
      }
    } else {
      this.addingValues[i] = +this.ndcList[i].adding;
    }
    this.adjustSOH(this.ndcList[i]?.gpi);
    this.editRow[i] = false;
  }

  openPinDialog(): void {
    this.showPinDialog = true;
    this.focusPin = true;
  }

  anyEditModeOpen(): boolean {
    return this.editRow.some(el => el === true);
  }

  validatePinRequest({success, pin}): void {
    if (success) {
      this.addOrder(pin);
    }
  }

  addOrder(pin: string): void {
    const transactions: ITransactionRequest[] = this.ndcList.map((el: INDC) => {
      return {
        pin: +pin,
        pharmacyId: null, // will be updated in the store
        drugId: el.id,
        rxNumber: '',
        patientName: '',
        addedQuantity: el.adding,
        activityType: ActivityTypesEnum.Received,
        purchaseOrderNumber: this.collectedOrderDetails?.poNumber,
        invoiceNumber: this.collectedOrderDetails?.invoiceNumber,
        shipDate: this.collectedOrderDetails?.shipDate,
        transactionDate: this.transactionDate,
        inventoryLocationID: el.locationId,
        csos: this.collectedOrderDetails?.csos,
        schedule: el.schedule,
        supplierDetailsID: this.collectedOrderDetails?.supplierDetails?.id,
        buyerDetailsID: this.collectedOrderDetails?.buyerDetails?.id,
        isBackCountMatches: el.isBackCountMatches,
        note: el.note
      };
    });
    this.store.dispatch(new AddEDIOrder(transactions))
      .pipe(take(1), withLatestFrom(this.orderStateSuccess$))
      .subscribe(([_, orderStateSuccess]) => {
        if (orderStateSuccess) {
          this.store.dispatch(new ClearLoadedLibertyRxInventory());
        }
        this.showPinDialog = false;
        this.focusPin = false;
        const added = this.ndcList.length;
        this.ndcList = [];
        this.resetLocationsAndBackCounts();
        this.collectedOrderDetails = null;
        this.backCountService.handleBackCountPillUpdate(transactions);
        this.router.navigate(['inventory', 'ndc'], { queryParams: { bulkAdded: added } });
      });
  }

  removeNDC(index: number): void {
    const ndc = this.ndcList[index];
    this.store.dispatch(new RemoveLoadedLibertyRxInventoryRow(ndc));
    this.ndcList.splice(index, 1);
    (this.addInventoryControlsForm.get('locationsAndBackCounts') as FormArray).removeAt(index);
    this.adjustSOH(ndc.gpi);
  }

  cancelAll(): void {
    this.store.dispatch(new ClearLoadedLibertyRxInventory());
    this.ndcList = [];
    this.resetLocationsAndBackCounts();
  }

  closeDialog(): void {
    this.showPinDialog = false;
    this.focusPin = false;
  }

  optionSelected(option: IDrug): void {
    this.currentOption = option;
    this.focusDrugSearch = false;
    this.focusQuantity = true;
  }

  sortByColumn(columnName: string): void {
    this.ndcList = this.sort(this.ndcList, columnName);
    if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
        (this.addInventoryControlsForm.get('locationsAndBackCounts') as FormArray).clear();
        this.ndcList.map((x: IBulkAddStateNdcItem) => {
          this.appendLocationAndBackCount(x?.id, x.locationId, x.schedule, x.isBackCountMatches);
      });
    }
  }

  openResetPinDialog(): void {
    this.showPinDialog = false;
    this.focusPin = false;
    this.showResetPinDialog = true;
  }

  closeResetPinDialog(): void {
    this.showResetPinDialog = false;
    this.showPinDialog = true;
    this.focusPin = true;
  }

  afterPinUpdated(success: boolean): void {
    // TODO: show message about pin update
    if (success) {
      this.closeResetPinDialog();
    }
  }

  hideNoLibertySettingPopover() {
    this.store.dispatch(new DisplayNoLibertySettingPopover(false));
  }

  hideNoNewNdcPopover() {
    this.store.dispatch(new SetUserAcknowledgedLibertyRxLoadStatus());
    this.store.dispatch(new DisplayNoNewNdcPopover(false));
  }

  hideLoadInventoryErrorModal() {
    this.store.dispatch(new SetUserAcknowledgedLibertyRxLoadStatus());
    this.store.dispatch(new DisplayLoadLibertyRxInventoryErrorModal(false));
  }

  hideLoadInventoryCancelModal() {
    this.store.dispatch(new SetUserAcknowledgedLibertyRxLoadStatus());
    this.store.dispatch(new DisplayLoadLibertyRxInventoryCancelModal(false));
  }

  now(): Date {
    return new Date();
  }

  dateTimeChanged(date): void {
    this.transactionDate = date.value;
  }
  private resetLocationsAndBackCounts(): void {
    (this.addInventoryControlsForm.get('locationsAndBackCounts') as FormArray).clear();
  }

  private appendLocationAndBackCount(drugId: string, locationId: string = null, schedule: DrugScheduleEnum, isBackCountMatches: boolean = null): void {
      const locationsAndBackCounts = this.addInventoryControlsForm.controls.locationsAndBackCounts as FormArray<FormGroup<IBulkAddInventoryLocationAndBackCountControl>>;

      const group = this.fb.group({
        id: [locationId, [Validators.required]],
        drugId,
        isBackCountMatches: [
          isBackCountMatches,
          this.backCountService.enabledForActivityAndSchedule(
            ActivityTypesEnum.Received,
            schedule,
            drugId
          )
            ? Validators.required
            : Validators.nullValidator,
        ],
      });
      group.get('id').valueChanges.pipe(untilDestroyed(this))
      .subscribe(x => {
        this.updateExcludeOptionsForAllRows();
      });
      locationsAndBackCounts.push(group);
      this.updateExcludeOptionsForAllRows();
  }

  locationChosen(data, index: number): void {
    this.ndcList[index].locationId = data.id;
    this.updateNdcStartOnHand(data.id, this.ndcList[index].id);
    this.checkNdcExistsInUnresolvedBackCount(this.ndcList[index]);
  }

  backCounted(value: boolean, drugId: string, locationId: string) {
    const ndc = this.getNdc(locationId, drugId);
    if (ndc) {
      ndc.isBackCountMatches = value;
    }
  }

  submitClicked() {
   if (this.addInventoryControlsForm.get('locationsAndBackCounts').invalid) {
    highlightRequiredControlsWithErrors(this.addInventoryControlsForm.get('locationsAndBackCounts') as any);
    return;
   } else {
      this.openPinDialog();
    }
  }

  addNote(note: string, locationId: string, drugId: string): void {
    const ndc = this.getNdc(locationId, drugId);
    if (ndc) {
      ndc.note = note;
    }
  }

  removeNote(locationId: string, drugId: string): void {
    const ndc = this.getNdc(locationId, drugId);
    if (ndc) {
      ndc.note = null;
    }
  }

  private getNdc(locationId: string, drugId: string) {
    return this.ndcList.find(x => x.locationId === locationId && x.id === drugId);
  }

  private checkNdcExistsInUnresolvedBackCount(ndc: INDC): void {
    if (ndc.locationId && this.backCountService.isAnyRequiresBackCount()) { 
      this.store.dispatch(new IsUnconfirmedBackCountNdcExists(ndc.locationId, ndc.id))
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        ndc.isUnresolvedBackCountExists = this.store.selectSnapshot(AuditsState.response)?.value;
      })
    }
 }


  private bulkCheckNDCsInActiveBackCount(): void {
    if (this.backCountService.isAnyRequiresBackCount() && this.ndcList?.length) {
      this.store.dispatch(new BulkGetNdcsExistInUnconfirmedBackCount(this.ndcList?.filter(x => x.locationId).map(x => ({drugID: x.id, locationID: x.locationId}))))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const activeBackCountNdcs = this.store.selectSnapshot(AuditsState.currentUnconfirmedNdcsInBackCount);
        activeBackCountNdcs.forEach(val => {
          const ndc = this.ndcList.find(x => x.locationId === x.locationId && x.id === val.drugID);
          if (ndc) {
            ndc.isUnresolvedBackCountExists = true;
          }
        });
      })
    }
  }
  
  private preserveState(): void {
    const state = {
      ndcs: this.ndcList as IBulkAddStateNdcItem[] || null, orderDetails: this.collectedOrderDetails, 
    };
    const prevState = this.store.selectSnapshot(ApplicationState.bulkAddInventorySnapshot);
    if (!isEqual(prevState, state)) {
      this.store.dispatch(new SaveBulkAddInventorySnapshot(state))
    }
  }

  ngOnDestroy(): void {
    //save state
    this.preserveState();
  }
}
